/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { ActivityLog, VfxShot } from '@/types/database'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get auth token from Supabase
 */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  return session.access_token
}

/**
 * Hook to get total clip count across all projects
 */
export function useTotalClips() {
  return useQuery({
    queryKey: ['dashboard', 'totalClips'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('clips')
        .select('*', { count: 'exact', head: true })

      if (error) throw error
      return count || 0
    },
  })
}

/**
 * Hook to get VFX shots statistics
 */
export function useVfxShotsStats() {
  return useQuery({
    queryKey: ['dashboard', 'vfxShots'],
    queryFn: async () => {
      // Get all VFX shots
      const { data, error } = await supabase
        .from('vfx_shots')
        .select('status')

      if (error) throw error

      const shots = data as Pick<VfxShot, 'status'>[]

      // Count by status
      const inProgress = shots.filter(
        (s) => s.status === 'in_progress' || s.status === 'review_ready'
      ).length
      const approved = shots.filter((s) => s.status === 'approved').length
      const total = shots.length

      return {
        total,
        inProgress,
        approved,
      }
    },
  })
}

/**
 * Hook to get activity logs from the last 7 days across all projects
 */
export function useRecentActivity() {
  return useQuery({
    queryKey: ['dashboard', 'recentActivity'],
    queryFn: async () => {
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      const logs = data as ActivityLog[]

      return {
        count: logs.length,
        logs: logs.slice(0, 10), // Return top 10 for display
      }
    },
  })
}

/**
 * Hook to get timeline commits from the last 7 days
 */
export function useRecentCommits() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'recentCommits'],
    queryFn: async () => {
      if (!user) throw new Error('Not authenticated')

      const token = await getAuthToken()

      // Get all sequences with name and project info
      const { data: sequences, error: seqError } = await supabase
        .from('sequences')
        .select('id, name, project_id')

      if (seqError) throw seqError

      if (!sequences || sequences.length === 0) {
        return {
          count: 0,
          commits: [],
        }
      }

      // Fetch commits for all sequences
      const commitPromises = sequences.map(async (seq) => {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/commits/${seq.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )

          if (!response.ok) return []

          const data = await response.json()
          // Enrich commits with sequence info
          return (data.commits || []).map((commit: any) => ({
            ...commit,
            sequence_name: seq.name,
            project_id: seq.project_id,
          }))
        } catch (error) {
          console.error(`Failed to fetch commits for sequence ${seq.id}:`, error)
          return []
        }
      })

      const allCommitsArrays = await Promise.all(commitPromises)
      const allCommits = allCommitsArrays.flat()

      // Filter commits from last 30 days (extended from 7 for more visibility)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const recentCommits = allCommits.filter((commit: any) => {
        if (!commit.created_at) return false
        const commitDate = new Date(commit.created_at)
        return commitDate >= thirtyDaysAgo
      })

      // Sort by date, newest first
      const sortedCommits = recentCommits.sort((a: any, b: any) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })

      return {
        count: recentCommits.length,
        commits: sortedCommits.slice(0, 10), // Return top 10 for display
      }
    },
    enabled: !!user,
  })
}

/**
 * Helper to format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffMs / 604800000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
  if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`

  return date.toLocaleDateString()
}

/**
 * Helper to get action icon and color from activity log
 */
export function getActivityIcon(actionType: string): { icon: string; color: string } {
  const iconMap: Record<string, { icon: string; color: string }> = {
    clip_import: { icon: 'Film', color: 'text-green-500' },
    clip_update: { icon: 'Film', color: 'text-blue-500' },
    clip_delete: { icon: 'Film', color: 'text-red-500' },
    project_update: { icon: 'FolderOpen', color: 'text-purple-500' },
    sequence_create: { icon: 'GitCommit', color: 'text-blue-500' },
    otio_clip_link: { icon: 'Link', color: 'text-green-500' },
    otio_clip_create: { icon: 'Film', color: 'text-green-500' },
  }

  return iconMap[actionType] || { icon: 'Activity', color: 'text-gray-500' }
}

/**
 * Helper to format activity log action
 */
export function formatActivityAction(log: ActivityLog): string {
  const metadata = log.action_metadata as any

  switch (log.action_type) {
    case 'clip_import':
      const created = metadata?.created || 0
      const updated = metadata?.updated || 0
      const parts = []
      if (created > 0) parts.push(`${created} created`)
      if (updated > 0) parts.push(`${updated} updated`)
      return parts.length > 0 ? `ALE import: ${parts.join(', ')}` : 'Imported ALE file'
    case 'clip_update':
      return 'Updated clip'
    case 'clip_delete':
      return 'Deleted clip'
    case 'project_create':
      const projectName = metadata?.project_name
      return projectName ? `Created project: ${projectName}` : 'Created project'
    case 'project_update':
      return 'Updated project'
    case 'sequence_create':
      return 'Created sequence'
    case 'otio_clip_link':
      const linkedCount = metadata?.clips_count || 0
      return `OTIO Timeline: ${linkedCount} clip${linkedCount !== 1 ? 's' : ''} linked`
    case 'otio_clip_create':
      const createdCount = metadata?.clips_count || 0
      return `OTIO Timeline: ${createdCount} new clip${createdCount !== 1 ? 's' : ''}`
    case 'vfx_shots_detected':
      const vfxDetected = metadata?.shots_detected || 0
      const vfxCreated = metadata?.shots_created || 0
      const vfxUpdated = metadata?.shots_updated || 0
      const vfxParts = []
      if (vfxCreated > 0) vfxParts.push(`${vfxCreated} created`)
      if (vfxUpdated > 0) vfxParts.push(`${vfxUpdated} updated`)
      return `VFX: ${vfxDetected} detected${vfxParts.length > 0 ? ` (${vfxParts.join(', ')})` : ''}`
    default:
      return log.action_type.replace(/_/g, ' ')
  }
}

// =============================================
// PROJECT-SPECIFIC DASHBOARD STATS
// =============================================

/**
 * Hook to get clip count for a specific project
 */
export function useProjectClips(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'projectClips', projectId],
    queryFn: async () => {
      if (!projectId) return 0

      const { count, error } = await supabase
        .from('clips')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)

      if (error) throw error
      return count || 0
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to get VFX shots statistics for a specific project
 */
export function useProjectVfxShotsStats(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'projectVfxShots', projectId],
    queryFn: async () => {
      if (!projectId) return { total: 0, inProgress: 0, approved: 0 }

      const { data, error } = await supabase
        .from('vfx_shots')
        .select('status')
        .eq('project_id', projectId)

      if (error) throw error

      const shots = data as Pick<VfxShot, 'status'>[]

      const inProgress = shots.filter(
        (s) => s.status === 'in_progress' || s.status === 'review_ready'
      ).length
      const approved = shots.filter((s) => s.status === 'approved').length
      const total = shots.length

      return {
        total,
        inProgress,
        approved,
      }
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to get activity logs for a specific project (last 30 days)
 */
export function useProjectActivity(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'projectActivity', projectId],
    queryFn: async () => {
      if (!projectId) return { count: 0, logs: [] }

      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('project_id', projectId)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      const logs = data as ActivityLog[]

      return {
        count: logs.length,
        logs: logs.slice(0, 10), // Return top 10 for display
      }
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to get commits for a specific project (last 30 days)
 */
export function useProjectCommits(projectId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'projectCommits', projectId],
    queryFn: async () => {
      if (!user || !projectId) {
        return { count: 0, commits: [] }
      }

      try {
        const token = await getAuthToken()

        // Get sequences for this project
        const { data: sequences, error: seqError } = await supabase
          .from('sequences')
          .select('id, name')
          .eq('project_id', projectId)

        if (seqError) throw seqError

        if (!sequences || sequences.length === 0) {
          return {
            count: 0,
            commits: [],
          }
        }

        // Fetch commits for all sequences in this project
        const commitPromises = sequences.map(async (seq) => {
          try {
            const response = await fetch(
              `${BACKEND_URL}/api/commits/${seq.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )

            if (!response.ok) {
              return []
            }

            const data = await response.json()

            // Enrich commits with sequence info
            return (data.commits || []).map((commit: any) => ({
              ...commit,
              sequence_name: seq.name,
            }))
          } catch {
            return []
          }
        })

        const allCommitsArrays = await Promise.all(commitPromises)
        const allCommits = allCommitsArrays.flat()

        // Filter commits from last 30 days
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

        const recentCommits = allCommits.filter((commit: any) => {
          if (!commit.created_at) return false
          const commitDate = new Date(commit.created_at)
          return commitDate >= thirtyDaysAgo
        })

        // Sort by date, newest first
        const sortedCommits = recentCommits.sort((a: any, b: any) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })

        return {
          count: recentCommits.length,
          commits: sortedCommits.slice(0, 10), // Return top 10 for display
        }
      } catch (error) {
        console.error('[useProjectCommits] Error:', error)
        throw error
      }
    },
    enabled: !!user && !!projectId,
    retry: 1, // Only retry once
    staleTime: 30000, // Consider data fresh for 30 seconds
  })
}

/**
 * Hook to get sequence count for a specific project
 */
export function useProjectSequences(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'projectSequences', projectId],
    queryFn: async () => {
      if (!projectId) return 0

      const { count, error } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)

      if (error) throw error
      return count || 0
    },
    enabled: !!projectId,
  })
}

// =============================================
// TEST/REFERENCE HOOKS - NOT FOR PRODUCTION USE
// =============================================
// ⚠️ These hooks were created for test/reference purposes only
// They are used in AssignedToMeSection.tsx which is also test code
//
// For ACTUAL assignment functionality, use:
// - useProjectAssignedClips(projectId)
// - useProjectAssignedVfxShots(projectId)

/**
 * ⚠️ TEST HOOK - DO NOT USE
 *
 * Hook to get clips assigned to the current user across ALL projects
 * This was test/reference code. Use useProjectAssignedClips instead.
 */
export function useMyAssignedClips() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'myAssignedClips', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('clips')
        .select(`
          *,
          project:projects(id, name)
        `)
        .eq('assigned_user_id', user.id)
        .order('assigned_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    enabled: !!user?.id,
  })
}

/**
 * ⚠️ TEST HOOK - DO NOT USE
 *
 * Hook to get VFX shots assigned to the current user across ALL projects
 * This was test/reference code. Use useProjectAssignedVfxShots instead.
 */
export function useMyAssignedVfxShots() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'myAssignedVfxShots', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('vfx_shots')
        .select(`
          *,
          project:projects(id, name)
        `)
        .eq('assigned_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    enabled: !!user?.id,
  })
}

// =============================================
// PROJECT DASHBOARD - PROJECT-SPECIFIC ASSIGNMENTS
// =============================================
// These hooks are for the project dashboard at /projects/:id/dashboard

/**
 * Hook to get clips assigned to the current user for a specific project
 */
export function useProjectAssignedClips(projectId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'projectAssignedClips', projectId, user?.id],
    queryFn: async () => {
      if (!user?.id || !projectId) throw new Error('User or project not specified')

      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('project_id', projectId)
        .eq('assigned_user_id', user.id)
        .order('assigned_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    enabled: !!user?.id && !!projectId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Always refetch when component mounts
  })
}

/**
 * Hook to get VFX shots assigned to the current user for a specific project
 */
export function useProjectAssignedVfxShots(projectId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['dashboard', 'projectAssignedVfxShots', projectId, user?.id],
    queryFn: async () => {
      if (!user?.id || !projectId) throw new Error('User or project not specified')

      const { data, error } = await supabase
        .from('vfx_shots')
        .select('*')
        .eq('project_id', projectId)
        .eq('assigned_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    enabled: !!user?.id && !!projectId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Always refetch when component mounts
  })
}
